import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  IArchivedTaskDTO,
  IArchivingJobExperimentResultDTO,
  ResponseType,
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  RunUnarchiveDTO,
} from 'business/models/Models';
import ArchivingService from 'business/services/ArchivingService';

export const fetchArchivedTasksThunk = createAsyncThunk<IArchivedTaskDTO[], void>(
  'fetchArchivedTasksThunk',
  async (_, thunkApi) => {
    try {
      const archivingService = new ArchivingService();
      const result = await archivingService.fetchArchivedTasks();
      if (result.responseType === ResponseType.Success) {
        return result.value ?? [];
      }

      return thunkApi.rejectWithValue(result.message || 'Failed to fetch archived tasks');
    } catch {
      return thunkApi.rejectWithValue('Failed to fetch archived tasks');
    }
  },
);

export const fetchArchivedExperimentsThunk = createAsyncThunk<
  IArchivingJobExperimentResultDTO[],
  number[]
>('fetchArchivedExperimentsThunk', async (jobIds, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.fetchArchivedExperiments(jobIds);
    if (result.responseType === ResponseType.Success) {
      return result.value ?? [];
    }

    return thunkApi.rejectWithValue(
      result.message || 'Failed to fetch archived experiments',
    );
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch archived experiments');
  }
});

export const runUnarchiveThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  RunUnarchiveDTO
>('runUnarchiveThunk', async (request: RunUnarchiveDTO, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.startUnarchiving(request);
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue(result.message || 'Failed to start unarchive job');
  } catch {
    return thunkApi.rejectWithValue('Failed to start unarchive job');
  }
});

export const fetchActiveUnarchiveJobResultsThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  number | string
>('fetchActiveUnarchiveJobResultsThunk', async (jobId, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    // getActiveJobResults resolves by explicit jobId regardless of job operation.
    const result = await archivingService.fetchActiveArchivingJobResults(jobId);
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue(
      result.message || 'Failed to fetch unarchive job results',
    );
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch unarchive job results');
  }
});

export const fetchUndismissedUnarchivingJobResultsThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  void
>('fetchUndismissedUnarchivingJobResultsThunk', async (_, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.fetchUndismissedUnarchivingJobResults();
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue(
      result.message || 'Failed to fetch undismissed unarchiving job results',
    );
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch undismissed unarchiving job results');
  }
});
